import { useEffect, useRef, type ReactElement } from 'react';
import {
  Button as AriaButton,
  Link as AriaLink,
  type ButtonProps as AriaButtonProps,
  type LinkProps as AriaLinkProps
} from 'react-aria-components';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends Omit<AriaButtonProps, 'className' | 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  busy?: boolean;
  busyLabel?: string;
  /** Layout utilities only. */
  className?: string;
}

export interface ButtonLinkProps extends Omit<AriaLinkProps, 'className' | 'style'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Layout utilities only. */
  className?: string;
}

export const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-ink text-on-ink-strong border-ink',
  secondary: 'bg-surface text-ink border-line-strong',
  quiet: 'bg-transparent text-ink border-transparent',
  danger: 'bg-surface text-danger border-danger'
};

const SIZES: Record<ButtonSize, string> = {
  md: 'px-3 py-2 text-md',
  sm: 'px-2 py-1 text-sm'
};

// Every element sets its own border, margin, padding, background, font and
// appearance; nothing relies on preflight or the browser's defaults. Line-height
// is not here: Button adds leading-control at its call site, ButtonLink inherits.
// On touch screens every button is at least 44px tall: Apple's and WCAG 2.5.5's target size.
const BASE =
  'inline-flex items-center justify-center gap-2 m-0 border border-solid rounded-control font-ui appearance-none cursor-pointer no-underline touch:min-h-[44px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';

export function buttonClasses(variant: ButtonVariant, size: ButtonSize, extra = ''): string {
  return `${BASE} ${SIZES[size]} ${VARIANTS[variant]} data-[disabled]:opacity-50 data-[disabled]:cursor-default ${extra}`.trim();
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy,
  busyLabel,
  children,
  isDisabled,
  className,
  ...rest
}: ButtonProps): ReactElement {
  // React Aria filters aria-busy out of the DOM props, so set it on the node.
  const node = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (busy) node.current?.setAttribute('aria-busy', 'true');
    else node.current?.removeAttribute('aria-busy');
  }, [busy]);
  return (
    <AriaButton
      {...rest}
      ref={node}
      isDisabled={isDisabled || busy}
      // A <button> drew at the browser's form-control line-height; a ButtonLink inherits its parent's.
      className={buttonClasses(variant, size, `leading-control ${className ?? ''}`)}
    >
      {busy && busyLabel ? busyLabel : children}
    </AriaButton>
  );
}

export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  className,
  ...rest
}: ButtonLinkProps): ReactElement {
  return <AriaLink {...rest} className={buttonClasses(variant, size, className)} />;
}
